import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useChatContext } from 'stream-chat-react'
import { useUserInfo } from 'hooks/useUserInfo'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { useGetAdvisorDashboardQuery } from '__generated__/api-types-and-hooks'
import { useTenantId } from 'utils/useTenantId'
import { USER_ROLES } from 'config'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { Camera, Plus, Search, Trash2 } from 'lucide-react'

import { uploadFiles } from 'components/AppFileUploader/AppFileUploader.helper'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials } from 'components/Chat/helper'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'

interface GroupCreationProps {
  onClose: () => void
  onGroupCreated: (channel: any) => void
  chatUsers: any
}

const GroupCreation: React.FC<GroupCreationProps> = ({ onClose, onGroupCreated, chatUsers }) => {
  const [groupName, setGroupName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [users, setUsers] = useState<IAssigneeOptions[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const [groupIcon, setGroupIcon] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { client } = useChatContext()
  useUserInfo()
  const tenantId = useTenantId()
  const loggedInUser = useSelector((state: RootState) => state.user.user)
  const isAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)

  // Get business profile to access team members
  const { data: advisorData } = useGetAdvisorDashboardQuery({ tenantId })
  const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({ enabled: true })

  // Memoize assignee options to prevent infinite re-renders
  const assigneeOptions = useMemo(() => {
    let options: IAssigneeOptions[] = []

    if (!isAdvisor) {
      options = businessAssigneeOptions
    } else {
      const response = JSON.parse(advisorData?.getAdvisorDashboard?.data || '{}')
      options =
        response?.accounts?.map((account: any) => ({
          label: `${account?.firstName} ${account?.lastName}`,
          value: account?.id,
          user: {
            id: account?.id,
            firstName: account?.firstName,
            lastName: account?.lastName,
          },
        })) || []
    }

    // Filter out current user
    return options.filter((option) => option.value !== loggedInUser?.id)
  }, [isAdvisor, businessAssigneeOptions, advisorData, loggedInUser?.id])

  useEffect(() => {
    setUsers(assigneeOptions)
  }, [assigneeOptions])

  // Get selected members list
  const selectedMembersList = useMemo(() => {
    return users.filter((user) => selectedUsers.has(user.value))
  }, [users, selectedUsers])

  // Get unselected users filtered by search
  const unselectedUsers = useMemo(() => {
    return users.filter(
      (user) =>
        !selectedUsers.has(user.value) &&
        user.label.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [users, selectedUsers, searchQuery])

  const handleAddMember = (userId: string) => {
    const newSelected = new Set(selectedUsers)
    newSelected.add(userId)
    setSelectedUsers(newSelected)
  }

  const handleRemoveMember = (userId: string) => {
    const newSelected = new Set(selectedUsers)
    newSelected.delete(userId)
    setSelectedUsers(newSelected)
  }

  const handleIconClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Upload to S3 and store the resulting URL for Stream channel image
      try {
        const uploaded = await uploadFiles({
          files: [file],
          tenantId,
          clientId: '',
          userId: loggedInUser?.id,
        })
        setGroupIcon(uploaded?.[0]?.url || null)
      } catch (err) {
        console.error('Failed to upload group icon', err)
      }
    }
  }

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedUsers.size === 0) return

    setIsCreating(true)
    try {
      const selectedUsersIds = Array.from(selectedUsers)

      const customData: any = {
        members: [...selectedUsersIds, client.userID || 'unknown'],
        team: tenantId,
        name: groupName.trim(),
      }

      // Add image only if it exists
      if (groupIcon) {
        customData.image = groupIcon
      }

      // Use explicit channel ID so the channel is non-distinct and members can be updated later
      const channelId = `group_${tenantId}_${Date.now()}`

      // Create channel with name and image metadata
      const channel = client.channel('messaging', channelId, customData)
      await channel.create()

      // Watch the channel
      await channel.watch()

      onGroupCreated(channel)
    } catch (error) {
      console.error('Failed to create group:', error)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <AppModalBox
      onClose={onClose}
      classes="p-0"
      title={'New group'}
      titleFont="font-semibold"
      height="h-auto"
      width="w-[540px] max-w-md"
      wrapperClass="flex items-center justify-center"
    >
      <div className="bg-app-white-100 rounded-2xl shadow-xl w-full max-h-[90vh] flex flex-col">
        {/* Group Icon & Name */}
        <div className="px-6 py-6 border-b border-app-grey-25">
          <h3 className="text-sm font-medium text-app-grey-70 mb-4">Group Icon & Name</h3>
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center bg-app-grey-20 border-2 border-dashed border-app-grey-40 cursor-pointer hover:bg-app-grey-10 transition-colors duration-150 overflow-hidden"
              onClick={handleIconClick}
            >
              {groupIcon ? (
                <img
                  src={groupIcon}
                  alt="Group icon"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <Camera className="w-8 h-8 text-app-grey-55" />
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className="flex-1">
              <input
                type="text"
                placeholder="Group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="w-full px-4 py-2.5 border border-app-grey-25 rounded-lg text-sm placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Members Section - Only show when users are selected */}
        {selectedUsers.size > 0 && (
          <div className="px-6 py-4 border-b border-app-grey-25">
            <h3 className="text-sm font-medium text-app-grey-70 mb-4">Members</h3>
            <div className="max-h-40 overflow-y-auto pr-1">
              <div className="space-y-2">
                {selectedMembersList.map((member) => (
                  <div key={member.value} className="flex items-center gap-3 py-2">
                    <AppAvatar
                      size="md"
                      type="mixed"
                      shape="Rounded"
                      text={getInitials({
                        firstName: member.user?.firstName || '',
                        lastName: member.user?.lastName || '',
                      })}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-app-black-100 truncate">
                        {member.label}
                      </p>
                      {member.email && (
                        <p className="text-xs text-app-grey-55 truncate">{member.email}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveMember(member.value)}
                      className="p-1.5 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
                      aria-label="Remove member"
                    >
                      <Trash2 className="w-4 h-4 text-app-grey-55" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Add Members Section */}
        <div className="px-6 py-4 border-b border-app-grey-25">
          <h3 className="text-sm font-medium text-app-grey-70 mb-4">Add members</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-grey-55" />
            <input
              type="text"
              placeholder="Search name...."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-app-grey-20 border border-app-grey-25 rounded-lg text-sm placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
            />
          </div>
        </div>

        {/* Unselected Users List */}
        <div className="flex-1 overflow-y-auto max-h-64">
          <div className="px-6">
            {unselectedUsers.length > 0 ? (
              unselectedUsers.map((user) => (
                <div
                  key={user.value}
                  className="flex items-center gap-3 py-3 cursor-pointer hover:bg-app-grey-10 rounded-lg px-2"
                  onClick={() => handleAddMember(user.value)}
                >
                  <AppAvatar
                    size="md"
                    type="mixed"
                    shape="Rounded"
                    text={getInitials({
                      firstName: user.user?.firstName || '',
                      lastName: user.user?.lastName || '',
                    })}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-black-100 truncate">{user.label}</p>
                    {user.email && (
                      <p className="text-xs text-app-grey-55 truncate">{user.email}</p>
                    )}
                  </div>
                  <div className="w-5 h-5 rounded border-2 border-app-grey-30 bg-white flex items-center justify-center">
                    <Plus className="w-3 h-3 text-app-grey-55" />
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-app-grey-55">
                  {searchQuery ? 'No users found' : 'All users have been added'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-app-grey-25">
          <button
            onClick={handleCreateGroup}
            disabled={!groupName.trim() || selectedUsers.size === 0 || isCreating}
            className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors duration-150 ${
              !groupName.trim() || selectedUsers.size === 0 || isCreating
                ? 'bg-app-grey-20 text-app-grey-55 cursor-not-allowed'
                : 'bg-app-primary-60 text-app-white-100 hover:bg-app-primary-70'
            }`}
          >
            {isCreating ? 'Creating group...' : 'Create group'}
          </button>
        </div>
      </div>
    </AppModalBox>
  )
}

export default GroupCreation
